/**
 * Initialization utilities for app runtime
 */

import { appRuntime } from '../appRuntimeService';
import type { AppRuntimeConfig } from '../types';

/**
 * Initialize app runtime services
 * 
 * Call this function BEFORE React renders (in main.tsx) to ensure
 * all core services are ready when components mount.
 * 
 * This function:
 * 1. Initializes monitoring (if DSN provided)
 * 2. Reads query parameters from URL
 * 3. Syncs tenant context to monitoring
 * 4. Sets up navigation listeners
 * 
 * @param config - Configuration options
 * 
 * @example
 * ```typescript
 * // In main.tsx (before React renders)
 * initializeAppRuntime({
 *   sentryDsn: import.meta.env.VITE_SENTRY_DSN,
 *   environment: import.meta.env.MODE,
 *   appName: 'web-portal-2',
 *   debug: import.meta.env.DEV
 * });
 * 
 * // Then render React
 * createRoot(document.getElementById('root')!).render(
 *   <StrictMode>
 *     <App />
 *   </StrictMode>
 * );
 * ```
 */
export function initializeAppRuntime(config?: AppRuntimeConfig): void {
  appRuntime.initialize(config);
  
  // Log initialization for debugging
  if (config?.debug) {
    console.log('[initializeAppRuntime] Complete', {
      isInitialized: appRuntime.isInitialized,
      isMonitoringEnabled: appRuntime.isMonitoringEnabled,
      params: {
        clubId: appRuntime.clubId,
        tenantHandle: appRuntime.tenantHandle,
        userId: appRuntime.userId
      }
    });
  }
}
